/**
 * The app in your language.
 *
 * A small dictionary per language, keyed by a stable id. English is complete
 * and is the fallback for anything a language has not translated yet, so a
 * missing key never shows a raw id on screen.
 *
 * The language is a setting (appLanguage) and follows you to every device.
 */

import { useSession } from '@/stores/session';

export const APP_LANGUAGES: { id: string; name: string; native: string }[] = [
  { id: 'en', name: 'English', native: 'English' },
  { id: 'de', name: 'German', native: 'Deutsch' },
  { id: 'es', name: 'Spanish', native: 'Español' },
  { id: 'fr', name: 'French', native: 'Français' },
  { id: 'tr', name: 'Turkish', native: 'Türkçe' },
  { id: 'pt', name: 'Portuguese', native: 'Português' },
  { id: 'it', name: 'Italian', native: 'Italiano' },
  { id: 'pl', name: 'Polish', native: 'Polski' },
  { id: 'nl', name: 'Dutch', native: 'Nederlands' }
];

type Dict = Record<string, string>;

const en: Dict = {
  'nav.home': 'Home',
  'nav.parties': 'Parties',
  'nav.friends': 'Friends',
  'nav.chat': 'Chat',
  'nav.games': 'Games',
  'nav.notifications': 'Notifications',
  'nav.crew': 'Crew',
  'nav.discoverCrews': 'Discover crews',
  'nav.findCrew': 'Find a crew',
  'nav.badges': 'Badges',
  'nav.accountSettings': 'Account settings',
  'top.search': 'Search games, friends or parties…',
  'top.createParty': 'Create party',
  'settings.title': 'Settings',
  'settings.search': 'Search settings...',
  'settings.account': 'My account',
  'settings.profile': 'Profile',
  'settings.games': 'Games',
  'settings.connections': 'Connections',
  'settings.premium': 'Membership',
  'settings.privacy': 'Privacy & safety',
  'settings.activity': 'Activity privacy',
  'settings.voice': 'Voice & video',
  'settings.notifications': 'Notifications',
  'settings.appearance': 'Appearance',
  'settings.chat': 'Chat',
  'settings.text': 'Accessibility',
  'settings.keys': 'Keybinds',
  'settings.language': 'Language & time',
  'settings.addons': 'Addons',
  'settings.advanced': 'Advanced',
  'common.online': 'Online',
  'common.offline': 'Offline',
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.reset': 'Reset'
};

const de: Dict = {
  'nav.home': 'Start', 'nav.parties': 'Partys', 'nav.friends': 'Freunde', 'nav.chat': 'Chat', 'nav.games': 'Spiele',
  'nav.notifications': 'Benachrichtigungen', 'nav.crew': 'Crew', 'nav.discoverCrews': 'Crews entdecken', 'nav.findCrew': 'Crew finden',
  'nav.badges': 'Abzeichen', 'nav.accountSettings': 'Kontoeinstellungen',
  'top.search': 'Spiele, Freunde oder Partys suchen…', 'top.createParty': 'Party erstellen',
  'settings.title': 'Einstellungen', 'settings.search': 'Einstellungen durchsuchen...',
  'settings.account': 'Konto', 'settings.profile': 'Profil', 'settings.games': 'Spiele', 'settings.connections': 'Verbindungen',
  'settings.premium': 'Mitgliedschaft', 'settings.privacy': 'Privatsphäre & Sicherheit', 'settings.activity': 'Aktivitäts-Privatsphäre',
  'settings.voice': 'Sprache & Video', 'settings.notifications': 'Benachrichtigungen', 'settings.appearance': 'Darstellung',
  'settings.chat': 'Chat', 'settings.text': 'Barrierefreiheit', 'settings.keys': 'Tastenkürzel', 'settings.language': 'Sprache & Zeit',
  'settings.addons': 'Add-ons', 'settings.advanced': 'Erweitert',
  'common.online': 'Online', 'common.offline': 'Offline', 'common.save': 'Speichern', 'common.cancel': 'Abbrechen', 'common.reset': 'Zurücksetzen'
};

const es: Dict = {
  'nav.home': 'Inicio', 'nav.parties': 'Partidas', 'nav.friends': 'Amigos', 'nav.chat': 'Chat', 'nav.games': 'Juegos',
  'nav.notifications': 'Notificaciones', 'nav.crew': 'Crew', 'nav.discoverCrews': 'Descubrir crews', 'nav.findCrew': 'Buscar una crew',
  'nav.badges': 'Insignias', 'nav.accountSettings': 'Ajustes de cuenta',
  'top.search': 'Buscar juegos, amigos o partidas…', 'top.createParty': 'Crear partida',
  'settings.title': 'Ajustes', 'settings.search': 'Buscar ajustes...',
  'settings.account': 'Cuenta', 'settings.profile': 'Perfil', 'settings.games': 'Juegos', 'settings.connections': 'Conexiones',
  'settings.premium': 'Membresía', 'settings.privacy': 'Privacidad y seguridad', 'settings.activity': 'Privacidad de actividad',
  'settings.voice': 'Voz y vídeo', 'settings.notifications': 'Notificaciones', 'settings.appearance': 'Apariencia',
  'settings.chat': 'Chat', 'settings.text': 'Accesibilidad', 'settings.keys': 'Atajos de teclado', 'settings.language': 'Idioma y hora',
  'settings.addons': 'Complementos', 'settings.advanced': 'Avanzado',
  'common.online': 'En línea', 'common.offline': 'Desconectado', 'common.save': 'Guardar', 'common.cancel': 'Cancelar', 'common.reset': 'Restablecer'
};

const fr: Dict = {
  'nav.home': 'Accueil', 'nav.parties': 'Parties', 'nav.friends': 'Amis', 'nav.chat': 'Discussions', 'nav.games': 'Jeux',
  'nav.notifications': 'Notifications', 'nav.crew': 'Crew', 'nav.discoverCrews': 'Découvrir des crews', 'nav.findCrew': 'Trouver une crew',
  'nav.badges': 'Badges', 'nav.accountSettings': 'Paramètres du compte',
  'top.search': 'Rechercher des jeux, amis ou parties…', 'top.createParty': 'Créer une partie',
  'settings.title': 'Paramètres', 'settings.search': 'Rechercher un paramètre...',
  'settings.account': 'Compte', 'settings.profile': 'Profil', 'settings.games': 'Jeux', 'settings.connections': 'Connexions',
  'settings.premium': 'Abonnement', 'settings.privacy': 'Confidentialité et sécurité', 'settings.activity': "Confidentialité d'activité",
  'settings.voice': 'Voix et vidéo', 'settings.notifications': 'Notifications', 'settings.appearance': 'Apparence',
  'settings.chat': 'Discussions', 'settings.text': 'Accessibilité', 'settings.keys': 'Raccourcis', 'settings.language': 'Langue et heure',
  'settings.addons': 'Extensions', 'settings.advanced': 'Avancé',
  'common.online': 'En ligne', 'common.offline': 'Hors ligne', 'common.save': 'Enregistrer', 'common.cancel': 'Annuler', 'common.reset': 'Réinitialiser'
};

const tr: Dict = {
  'nav.home': 'Ana sayfa', 'nav.parties': 'Partiler', 'nav.friends': 'Arkadaşlar', 'nav.chat': 'Sohbet', 'nav.games': 'Oyunlar',
  'nav.notifications': 'Bildirimler', 'nav.crew': 'Ekip', 'nav.discoverCrews': 'Ekipleri keşfet', 'nav.findCrew': 'Ekip bul',
  'nav.badges': 'Rozetler', 'nav.accountSettings': 'Hesap ayarları',
  'top.search': 'Oyun, arkadaş veya parti ara…', 'top.createParty': 'Parti oluştur',
  'settings.title': 'Ayarlar', 'settings.search': 'Ayarlarda ara...',
  'settings.account': 'Hesap', 'settings.profile': 'Profil', 'settings.games': 'Oyunlar', 'settings.connections': 'Bağlantılar',
  'settings.premium': 'Üyelik', 'settings.privacy': 'Gizlilik ve güvenlik', 'settings.activity': 'Etkinlik gizliliği',
  'settings.voice': 'Ses ve görüntü', 'settings.notifications': 'Bildirimler', 'settings.appearance': 'Görünüm',
  'settings.chat': 'Sohbet', 'settings.text': 'Erişilebilirlik', 'settings.keys': 'Kısayollar', 'settings.language': 'Dil ve saat',
  'settings.addons': 'Eklentiler', 'settings.advanced': 'Gelişmiş',
  'common.online': 'Çevrimiçi', 'common.offline': 'Çevrimdışı', 'common.save': 'Kaydet', 'common.cancel': 'İptal', 'common.reset': 'Sıfırla'
};

const pt: Dict = {
  'nav.home': 'Início', 'nav.parties': 'Partidas', 'nav.friends': 'Amigos', 'nav.chat': 'Chat', 'nav.games': 'Jogos',
  'nav.notifications': 'Notificações', 'nav.crew': 'Crew', 'nav.discoverCrews': 'Descobrir crews', 'nav.findCrew': 'Encontrar uma crew',
  'nav.badges': 'Emblemas', 'nav.accountSettings': 'Configurações da conta',
  'top.search': 'Pesquisar jogos, amigos ou partidas…', 'top.createParty': 'Criar partida',
  'settings.title': 'Configurações', 'settings.search': 'Pesquisar configurações...',
  'settings.account': 'Conta', 'settings.profile': 'Perfil', 'settings.games': 'Jogos', 'settings.connections': 'Conexões',
  'settings.premium': 'Assinatura', 'settings.privacy': 'Privacidade e segurança', 'settings.activity': 'Privacidade de atividade',
  'settings.voice': 'Voz e vídeo', 'settings.notifications': 'Notificações', 'settings.appearance': 'Aparência',
  'settings.chat': 'Chat', 'settings.text': 'Acessibilidade', 'settings.keys': 'Atalhos', 'settings.language': 'Idioma e hora',
  'settings.addons': 'Complementos', 'settings.advanced': 'Avançado',
  'common.online': 'Online', 'common.offline': 'Offline', 'common.save': 'Salvar', 'common.cancel': 'Cancelar', 'common.reset': 'Redefinir'
};

const it: Dict = {
  'nav.home': 'Home', 'nav.parties': 'Party', 'nav.friends': 'Amici', 'nav.chat': 'Chat', 'nav.games': 'Giochi',
  'nav.notifications': 'Notifiche', 'nav.crew': 'Crew', 'nav.discoverCrews': 'Scopri crew', 'nav.findCrew': 'Trova una crew',
  'nav.badges': 'Distintivi', 'nav.accountSettings': "Impostazioni dell'account",
  'top.search': 'Cerca giochi, amici o party…', 'top.createParty': 'Crea party',
  'settings.title': 'Impostazioni', 'settings.search': 'Cerca impostazioni...',
  'settings.account': 'Account', 'settings.profile': 'Profilo', 'settings.games': 'Giochi', 'settings.connections': 'Connessioni',
  'settings.premium': 'Abbonamento', 'settings.privacy': 'Privacy e sicurezza', 'settings.activity': 'Privacy delle attività',
  'settings.voice': 'Voce e video', 'settings.notifications': 'Notifiche', 'settings.appearance': 'Aspetto',
  'settings.chat': 'Chat', 'settings.text': 'Accessibilità', 'settings.keys': 'Scorciatoie', 'settings.language': 'Lingua e ora',
  'settings.addons': 'Componenti aggiuntivi', 'settings.advanced': 'Avanzate',
  'common.online': 'Online', 'common.offline': 'Offline', 'common.save': 'Salva', 'common.cancel': 'Annulla', 'common.reset': 'Ripristina'
};

const pl: Dict = {
  'nav.home': 'Start', 'nav.parties': 'Party', 'nav.friends': 'Znajomi', 'nav.chat': 'Czat', 'nav.games': 'Gry',
  'nav.notifications': 'Powiadomienia', 'nav.crew': 'Ekipa', 'nav.discoverCrews': 'Odkryj ekipy', 'nav.findCrew': 'Znajdź ekipę',
  'nav.badges': 'Odznaki', 'nav.accountSettings': 'Ustawienia konta',
  'top.search': 'Szukaj gier, znajomych lub party…', 'top.createParty': 'Utwórz party',
  'settings.title': 'Ustawienia', 'settings.search': 'Szukaj w ustawieniach...',
  'settings.account': 'Konto', 'settings.profile': 'Profil', 'settings.games': 'Gry', 'settings.connections': 'Połączenia',
  'settings.premium': 'Członkostwo', 'settings.privacy': 'Prywatność i bezpieczeństwo', 'settings.activity': 'Prywatność aktywności',
  'settings.voice': 'Głos i wideo', 'settings.notifications': 'Powiadomienia', 'settings.appearance': 'Wygląd',
  'settings.chat': 'Czat', 'settings.text': 'Dostępność', 'settings.keys': 'Skróty klawiszowe', 'settings.language': 'Język i czas',
  'settings.addons': 'Dodatki', 'settings.advanced': 'Zaawansowane',
  'common.online': 'Online', 'common.offline': 'Offline', 'common.save': 'Zapisz', 'common.cancel': 'Anuluj', 'common.reset': 'Resetuj'
};

const nl: Dict = {
  'nav.home': 'Start', 'nav.parties': 'Party’s', 'nav.friends': 'Vrienden', 'nav.chat': 'Chat', 'nav.games': 'Games',
  'nav.notifications': 'Meldingen', 'nav.crew': 'Crew', 'nav.discoverCrews': 'Crews ontdekken', 'nav.findCrew': 'Crew zoeken',
  'nav.badges': 'Badges', 'nav.accountSettings': 'Accountinstellingen',
  'top.search': 'Zoek games, vrienden of party’s…', 'top.createParty': 'Party maken',
  'settings.title': 'Instellingen', 'settings.search': 'Instellingen zoeken...',
  'settings.account': 'Account', 'settings.profile': 'Profiel', 'settings.games': 'Games', 'settings.connections': 'Koppelingen',
  'settings.premium': 'Lidmaatschap', 'settings.privacy': 'Privacy en beveiliging', 'settings.activity': 'Activiteitsprivacy',
  'settings.voice': 'Spraak en video', 'settings.notifications': 'Meldingen', 'settings.appearance': 'Weergave',
  'settings.chat': 'Chat', 'settings.text': 'Toegankelijkheid', 'settings.keys': 'Sneltoetsen', 'settings.language': 'Taal en tijd',
  'settings.addons': 'Add-ons', 'settings.advanced': 'Geavanceerd',
  'common.online': 'Online', 'common.offline': 'Offline', 'common.save': 'Opslaan', 'common.cancel': 'Annuleren', 'common.reset': 'Herstellen'
};

const DICTS: Record<string, Dict> = { en, de, es, fr, tr, pt, it, pl, nl };

/** A string in a given language, falling back to English. */
export function translate(lang: string | undefined, key: string): string {
  return DICTS[lang ?? 'en']?.[key] ?? en[key] ?? key;
}

/** The translate function for the language you picked. Re-renders when it changes. */
export function useT(): (key: string) => string {
  const lang = useSession((s) => s.settings?.appLanguage ?? 'en');
  return (key: string) => translate(lang, key);
}
